import { Fragment } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

import MarkDown from '~/components/Helpers/MarkDown'
import Avatar, { AvatarProps } from '~/components/UI/General/Avatar/Avatar'
import Blockquote from '~/assets/icons/blockquote'

const cardVariants = cva(
  'relative p-xs-plus max-h-[500px] !h-[500px] flex flex-col overflow-hidden gap-m sm:p-s lg:p-m',
  {
    variants: {
      backgroundColour: {
        'ink-blue': 'bg-blue-d [&_svg]:stroke-blue-d-80 text-white',
        'mid-blue': 'bg-blue-m [&_svg]:stroke-blue-m-80 text-white',
        'light-blue': 'bg-blue-l-60 [&_svg]:stroke-blue-l-80 text-blue-d',
        chartreuse: 'bg-char [&_svg]:stroke-char-80 text-blue-d',
        'light-pink': 'bg-pink-60 [&_svg]:stroke-pink-80 text-blue-d',
        'light-aqua': 'bg-green-60 [&_svg]:stroke-green-80 text-blue-d',
        'light-purple': 'bg-purple-60 [&_svg]:stroke-purple-80 text-blue-d',
        'light-orange': 'bg-orange-60 [&_svg]:stroke-orange-80 text-blue-d',
        'light-grey': 'bg-grey-l [&_svg]:stroke-white text-blue-d',
        white: 'bg-white [&_svg]:stroke-grey-l text-blue-d'
      }
    },
    defaultVariants: {
      backgroundColour: 'ink-blue'
    }
  }
)

export type QuoteCardProps = CardProps & {
  content: {
    quote: string
    attribution?: string
    role?: string
    avatar?: Array<AvatarProps['avatar']>
  }
}

const QuoteCard = ({ content, carousel, className }: QuoteCardProps) => {
  const { backgroundColour, quote, attribution, role, avatar } = content

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  return (
    <div className={cn(cardVariants({ backgroundColour }), className)}>
      <Blockquote
        className={cn(
          carousel ? 'w-3xl' : 'w-[160px]',
          '-right-3xs -top-s absolute h-auto'
        )}
      />

      <MarkDown
        content={quote}
        components={{
          p: Fragment
        }}
        className='lg:!text-h3-lg !text-h3-sm sm:!text-h3-md z-0 flex grow items-end font-serif leading-[1.3] font-[400] italic'
      />

      {(attribution || role) && (
        <div className='gap-xs flex items-center'>
          {avatar && avatar.length > 0 && (
            <Avatar avatar={avatar[0]} colour={foregroundColour} />
          )}
          <div className='flex flex-col'>
            {attribution && <p className='font-bold'>{attribution}</p>}
            {role && <p>{role}</p>}
          </div>
        </div>
      )}
    </div>
  )
}

export default QuoteCard
